import { useState, useEffect, useRef } from "react";

const C = {
  cream: "#F5F0E8",
  creamDark: "#EDE7DB",
  creamDeep: "#E0D8CC",
  charcoal: "#3d3226",
  navy: "#4a3d30",
  navyLight: "#5a4d40",
  gold: "#C9A84C",
  goldLight: "#E2C97E",
  goldPale: "#F5EDDA",
  teal: "#2A9D8F",
  tealLight: "#4DB6A8",
  tealPale: "#E8F5F3",
  white: "#FFFFFF",
  textMuted: "#6B6560",
  textDark: "#2C1A0E",
  border: "#D8D0C4",
};

const NAV_LINKS = ["Services", "À propos", "Approche", "Contact"];

const SERVICES = [
  {
    icon: "🌐",
    title: "Sites Web & Applications",
    desc: "Des sites professionnels qui reflètent votre image et convertissent vos visiteurs en clients. E-commerce, vitrine, portail — sur mesure.",
    tags: ["Design", "E-commerce", "SEO"],
  },
  {
    icon: "🤖",
    title: "Intelligence Artificielle",
    desc: "Intégrez la puissance de l'IA dans votre entreprise : chatbots intelligents, analyse de données, automatisation des réponses clients.",
    tags: ["Chatbot IA", "Analyse", "Automation"],
  },
  {
    icon: "⚙️",
    title: "Automatisation & Workflows",
    desc: "Libérez votre équipe des tâches répétitives. Nous automatisons vos processus métier — facturation, relances, synchronisation de données.",
    tags: ["Zapier", "Make", "API"],
  },
  {
    icon: "☁️",
    title: "Solutions Cloud & Infra",
    desc: "Migration cloud, gestion de serveurs, sauvegarde sécurisée, messagerie professionnelle et outils collaboratifs.",
    tags: ["Cloud", "Sécurité", "Backup"],
  },
  {
    icon: "📊",
    title: "CRM & Outils Métiers",
    desc: "Mise en place de votre CRM, tableaux de bord, outils de gestion — tout centralisé pour gagner en efficacité.",
    tags: ["CRM", "Dashboard", "Reporting"],
  },
  {
    icon: "🛡️",
    title: "Conseil & Accompagnement IT",
    desc: "Audit de vos systèmes, recommandations stratégiques, formation de vos équipes et support continu au quotidien.",
    tags: ["Audit", "Formation", "Support"],
  },
];

const VALUES = [
  {
    icon: "🤝",
    title: "Proximité & Confiance",
    desc: "Une relation directe et humaine. Vous avez toujours un interlocuteur qui vous connaît et s'implique.",
  },
  {
    icon: "💡",
    title: "Accessibilité technologique",
    desc: "Les meilleurs outils — IA, cloud, automatisation — rendus accessibles à toutes les PME.",
  },
  {
    icon: "⚡",
    title: "Solutions sur mesure",
    desc: "Chaque entreprise est unique. Nous adaptons nos recommandations à vos besoins réels.",
  },
  {
    icon: "🔒",
    title: "Transparence totale",
    desc: "Des prix clairs, des délais honnêtes. Vous savez toujours où en est votre projet.",
  },
];

const STEPS = [
  {
    icon: "💬",
    num: "01",
    title: "Écoute",
    desc: "Un premier échange gratuit pour comprendre votre activité, vos besoins et vos objectifs.",
  },
  {
    icon: "📋",
    num: "02",
    title: "Proposition",
    desc: "Un devis clair et détaillé. Vous savez exactement ce que vous payez et ce que vous obtenez.",
  },
  {
    icon: "🔨",
    num: "03",
    title: "Réalisation",
    desc: "Notre équipe s'occupe de tout. Vous êtes informés à chaque étape, sans jargon technique.",
  },
  {
    icon: "🚀",
    num: "04",
    title: "Suivi continu",
    desc: "Après livraison, on reste là. Support, évolutions, formation — votre partenaire sur le long terme.",
  },
];

const COMPARE = [
  ["Interlocuteur dédié", "Rarement", "Toujours"],
  ["Délai de réponse", "3–10 jours", "24–48h"],
  ["Contrat minimum", "12 mois+", "Flexible"],
  ["Tarif", "Très élevé", "Accessible"],
  ["Connaissance de votre métier", "Partielle", "Approfondie"],
  ["Personnalisation", "Standardisé", "Sur mesure"],
];

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return scrolled;
}

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return width;
}

function useVisible(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return visible;
}

function FadeIn({ children, delay = 0, style = {} }) {
  const ref = useRef();
  const visible = useVisible(ref);
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Tag({ children }) {
  return (
    <span
      style={{
        fontSize: 11,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: C.teal,
        border: `1px solid ${C.teal}44`,
        padding: "3px 9px",
        borderRadius: 4,
        background: C.tealPale,
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

const ShalvaTechLogo = process.env.PUBLIC_URL + "/ShalvaTechLogo.svg";

export default function ShalvaTech() {
  const scrolled = useScrolled();
  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width < 1024;

  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    societe: "",
    service: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <div
      style={{
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        background: C.cream,
        color: C.textDark,
        overflowX: "hidden",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${C.gold}44; }
        .hover-card:hover { border-color: ${C.gold}88 !important; transform: translateY(-4px); box-shadow: 0 12px 40px ${C.charcoal}12 !important; }
        .hover-step:hover .step-circle { border-color: ${C.gold} !important; background: ${C.goldPale} !important; }
        .hover-row:hover { background: ${C.tealPale} !important; }
        a { color: inherit; text-decoration: none; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${C.teal} !important; box-shadow: 0 0 0 3px ${C.teal}18; }

        /* Mobile menu animation */
        .mobile-menu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.35s ease;
        }
        .mobile-menu.open {
          max-height: 400px;
        }

        /* Hamburger icon */
        .hamburger { cursor: pointer; display: flex; flex-direction: column; gap: 5px; padding: 4px; }
        .hamburger span {
          display: block; width: 22px; height: 2px;
          background: ${C.charcoal};
          transition: all 0.3s;
          transform-origin: center;
        }
        .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .hamburger.open span:nth-child(2) { opacity: 0; }
        .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
      `}</style>

      {/* ── NAV ── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: C.creamDark,
          borderBottom: `1px solid ${C.gold}28`,
          transition: "background 0.4s",
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            padding: isMobile ? "0 1.2rem" : "0 2rem",
            height: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ paddingTop: "18px", display: "flex", alignItems: "center" }}>
            <img width="90px" src={ShalvaTechLogo} alt="ShalvaTech" />
          </div>

          {/* Desktop links */}
          {!isTablet && (
            <div style={{ display: "flex", gap: "2.2rem", alignItems: "center" }}>
              {NAV_LINKS.map((l) => (
                <button
                  key={l}
                  onClick={() => scrollTo(l.toLowerCase().replace("à", "a"))}
                  style={{
                    background: "none",
                    border: "none",
                    color: `${C.charcoal}99`,
                    fontSize: 13,
                    fontWeight: "bold",
                    cursor: "pointer",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    transition: "color 0.2s",
                    padding: 0,
                  }}
                  onMouseEnter={(e) => (e.target.style.color = C.gold)}
                  onMouseLeave={(e) => (e.target.style.color = `${C.charcoal}99`)}
                >
                  {l}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                style={{
                  background: "transparent",
                  border: `1px solid ${C.gold}`,
                  color: C.gold,
                  padding: "0.5rem 1.3rem",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  transition: "all 0.3s",
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.gold;
                  e.currentTarget.style.color = C.charcoal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = C.gold;
                }}
              >
                Contact
              </button>
            </div>
          )}

          {/* Mobile / Tablet hamburger */}
          {isTablet && (
            <div
              className={`hamburger ${mobileOpen ? "open" : ""}`}
              onClick={() => setMobileOpen((o) => !o)}
              role="button"
              aria-label="Menu"
            >
              <span />
              <span />
              <span />
            </div>
          )}
        </div>

        {/* Mobile dropdown menu */}
        {isTablet && (
          <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
            <div
              style={{
                borderTop: `1px solid ${C.gold}22`,
                padding: "0.8rem 1.5rem 1.2rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.2rem",
              }}
            >
              {[...NAV_LINKS, "Contact"].map((l) => (
                <button
                  key={l}
                  onClick={() => scrollTo(l.toLowerCase().replace("à", "a"))}
                  style={{
                    background: "none",
                    border: "none",
                    color: C.charcoal,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    textAlign: "left",
                    padding: "0.7rem 0",
                    borderBottom: `1px solid ${C.border}55`,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.color = C.teal)}
                  onMouseLeave={(e) => (e.target.style.color = C.charcoal)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section
        id="hero"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          background: `linear-gradient(160deg, ${C.charcoal} 0%, ${C.navy} 100%)`,
          padding: isMobile ? "7rem 1.3rem 3rem" : isTablet ? "8rem 2rem 4rem" : "8rem 2rem 4rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 60% 50% at 75% 55%, ${C.teal}12 0%, transparent 65%), radial-gradient(ellipse 40% 40% at 15% 75%, ${C.gold}09 0%, transparent 55%)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 320,
            height: 320,
            borderRadius: "50%",
            border: `1px solid ${C.gold}12`,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            width: "100%",
            display: "grid",
            gridTemplateColumns: isTablet ? "1fr" : "1.1fr 0.9fr",
            gap: isTablet ? "2.5rem" : "4rem",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-block",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: C.gold,
                border: `1px solid ${C.gold}44`,
                padding: "5px 14px",
                marginBottom: "1.5rem",
                borderRadius: 3,
                background: `${C.gold}0e`,
              }}
            >
              Solutions Technologiques · PME
            </div>
            <h1
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2.6rem" : "clamp(2.8rem, 5vw, 4.4rem)",
                lineHeight: 1.1,
                fontWeight: 600,
                color: C.cream,
                marginBottom: "1.5rem",
              }}
            >
              La technologie,
              <br />
              <em style={{ color: C.goldLight, fontStyle: "italic" }}>
                enfin sereine
              </em>
              <br />
              pour votre affaire.
            </h1>
            <p
              style={{
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                color: `${C.cream}88`,
                maxWidth: 460,
                lineHeight: 1.85,
                marginBottom: "2.5rem",
              }}
            >
              ShalvaTech accompagne les petites et moyennes entreprises avec des
              outils numériques modernes — accessibles, abordables, et conçus
              pour vous simplifier la vie.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                onClick={() => scrollTo("services")}
                style={{
                  background: C.gold,
                  color: C.charcoal,
                  padding: "0.9rem 2rem",
                  border: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  transition: "all 0.3s",
                  borderRadius: 4,
                  textAlign: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.goldLight;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = C.gold;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Découvrir nos solutions
              </button>
              <button
                onClick={() => scrollTo("contact")}
                style={{
                  background: "transparent",
                  color: C.cream,
                  padding: "0.9rem 2rem",
                  border: `1px solid ${C.cream}44`,
                  fontSize: 13,
                  cursor: "pointer",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  transition: "all 0.3s",
                  borderRadius: 4,
                  textAlign: "center",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.cream)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${C.cream}44`)}
              >
                Prendre rendez-vous
              </button>
            </div>
          </div>

          {/* Hero card — masqué sur mobile pour alléger */}
          {!isMobile && (
            <div
              style={{
                background: C.creamDark,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                overflow: "hidden",
                boxShadow: `0 24px 60px ${C.charcoal}33`,
              }}
            >
              <div
                style={{
                  background: C.creamDark,
                  padding: "0 1.2rem",
                  display: "flex",
                  maxHeight: "80px",
                  alignItems: "start",
                  justifyContent: "space-between",
                  borderBottom: `2px solid ${C.gold}44`,
                }}
              >
                <img width="90px" src={ShalvaTechLogo} alt="ShalvaTech" />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "25px",
                    color: C.charcoal,
                    letterSpacing: "0.05em",
                  }}
                >
                  Tableau de bord
                </span>
                <div style={{ display: "flex", gap: 5, padding: "25px" }}>
                  {["#E05252", "#E09E52", "#52C288"].map((c) => (
                    <div
                      key={c}
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: c,
                      }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ padding: "1.2rem" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "0.8rem",
                    marginBottom: "1rem",
                  }}
                >
                  {[
                    { label: "Clients actifs", val: "247", color: C.teal },
                    { label: "Projets en cours", val: "12", color: C.gold },
                    { label: "Satisfaction", val: "98%", color: "#3DAA72" },
                  ].map((m) => (
                    <div
                      key={m.label}
                      style={{
                        background: C.white,
                        borderRadius: 8,
                        padding: "0.9rem",
                        border: `1px solid ${C.border}`,
                        borderTop: `3px solid ${m.color}`,
                      }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.charcoal }}>
                        {m.val}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.textMuted,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          marginTop: 2,
                        }}
                      >
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    background: C.white,
                    borderRadius: 8,
                    border: `1px solid ${C.border}`,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "0.6rem 0.9rem",
                      borderBottom: `1px solid ${C.creamDeep}`,
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Dernières activités
                  </div>
                  {[
                    { t: "Site e-commerce livré", s: "Boulangerie Martin", dot: C.teal },
                    { t: "Automatisation factures", s: "Cabinet Durand", dot: C.gold },
                    { t: "CRM configuré", s: "Agence Bloom", dot: "#3DAA72" },
                  ].map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "0.6rem 0.9rem",
                        borderBottom: i < 2 ? `1px solid ${C.creamDeep}` : "none",
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: r.dot,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: C.charcoal }}>
                          {r.t}
                        </div>
                        <div style={{ fontSize: 10.5, color: C.textMuted }}>{r.s}</div>
                      </div>
                      <div style={{ fontSize: 10, color: C.teal, fontWeight: 500 }}>
                        ✓ Actif
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div
        style={{
          background: C.charcoal,
          borderTop: `1px solid ${C.gold}22`,
          borderBottom: `1px solid ${C.gold}22`,
          overflow: "hidden",
          padding: "0.85rem 0",
        }}
      >
        <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
        <div
          style={{
            display: "flex",
            gap: "3.5rem",
            whiteSpace: "nowrap",
            animation: "marquee 28s linear infinite",
          }}
        >
          {[
            "Intelligence Artificielle", "Transformation Digitale", "Sites & Applications",
            "Automatisation", "Solutions Cloud", "Conseil IT", "CRM & Outils Métiers", "Cybersécurité",
            "Intelligence Artificielle", "Transformation Digitale", "Sites & Applications",
            "Automatisation", "Solutions Cloud", "Conseil IT", "CRM & Outils Métiers", "Cybersécurité",
          ].map((t, i) => (
            <span
              key={i}
              style={{
                fontSize: 11.5,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: `${C.cream}66`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <span
                style={{
                  width: 4, height: 4, borderRadius: "50%",
                  background: C.gold, display: "inline-block",
                }}
              />{" "}
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* ── À PROPOS ── */}
      <section
        id="a-propos"
        style={{ padding: isMobile ? "4rem 1.3rem" : "6rem 2rem", background: C.cream }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr",
            gap: isTablet ? "3rem" : "5rem",
            alignItems: "center",
          }}
        >
          <FadeIn>
            <span
              style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: C.gold, display: "block", marginBottom: "0.8rem",
              }}
            >
              Notre mission
            </span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2rem" : "clamp(2rem, 3.5vw, 2.8rem)",
                fontWeight: 600,
                lineHeight: 1.2,
                color: C.charcoal,
                marginBottom: "1.2rem",
              }}
            >
              Une équipe à votre service,
              <br />
              <em style={{ color: C.teal, fontStyle: "italic" }}>pas une machine</em>
            </h2>
            <p style={{ color: C.textMuted, lineHeight: 1.9, marginBottom: "1rem" }}>
              Chez ShalvaTech, nous partons d'un constat simple :{" "}
              <strong style={{ color: C.charcoal }}>
                les PME méritent autant que les grandes entreprises
              </strong>{" "}
              en matière d'outils technologiques — sans avoir à dépenser des fortunes ni se
              perdre dans la complexité.
            </p>
            <div
              style={{
                borderLeft: `3px solid ${C.gold}`,
                paddingLeft: "1.3rem",
                margin: "1.8rem 0",
                background: C.goldPale,
                padding: "1rem 1.3rem",
                borderRadius: "0 6px 6px 0",
              }}
            >
              <p
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.2rem",
                  fontStyle: "italic",
                  color: C.charcoal,
                  lineHeight: 1.5,
                }}
              >
                "Vous n'avez pas besoin d'une grande entreprise. Vous avez besoin
                d'une équipe fiable qui comprend vos enjeux."
              </p>
            </div>
            <p style={{ color: C.textMuted, lineHeight: 1.9 }}>
              Notre nom dit tout.{" "}
              <strong style={{ color: C.charcoal }}>Shalva (שַׁלְוָה)</strong> signifie{" "}
              <em>tranquillité, sérénité</em> — c'est exactement ce que nous vous apportons.
            </p>
          </FadeIn>
          <FadeIn delay={isTablet ? 0 : 0.15}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {VALUES.map((v, i) => (
                <div
                  key={i}
                  className="hover-card"
                  style={{
                    display: "flex",
                    gap: "1rem",
                    padding: "1.2rem 1.3rem",
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    transition: "all 0.3s",
                    boxShadow: `0 2px 8px ${C.charcoal}06`,
                  }}
                >
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: 8,
                      background: C.goldPale,
                      border: `1px solid ${C.gold}44`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.1rem", flexShrink: 0,
                    }}
                  >
                    {v.icon}
                  </div>
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: C.charcoal, marginBottom: 3 }}>
                      {v.title}
                    </h4>
                    <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section
        id="services"
        style={{ padding: isMobile ? "4rem 1.3rem" : "6rem 2rem", background: C.creamDark }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <FadeIn style={{ textAlign: "center", maxWidth: 600, margin: "0 auto 3.5rem" }}>
            <span
              style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: C.gold, display: "block", marginBottom: "0.8rem",
              }}
            >
              Ce que nous faisons
            </span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2rem" : "clamp(2rem, 3.5vw, 2.8rem)",
                fontWeight: 600, lineHeight: 1.2, color: C.charcoal, marginBottom: "1rem",
              }}
            >
              Des solutions pensées pour votre réalité
            </h2>
            <p style={{ fontSize: "1rem", color: C.textMuted, lineHeight: 1.8 }}>
              Qu'il s'agisse de moderniser votre présence en ligne, d'automatiser vos processus
              ou d'intégrer l'IA, nous vous guidons à chaque étape.
            </p>
          </FadeIn>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3, 1fr)",
              gap: "1.3rem",
            }}
          >
            {SERVICES.map((s, i) => (
              <FadeIn key={i} delay={i * 0.07}>
                <div
                  className="hover-card"
                  style={{
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: "2rem",
                    transition: "all 0.35s",
                    boxShadow: `0 2px 12px ${C.charcoal}06`,
                    height: "100%",
                    cursor: "default",
                    borderTop: `3px solid ${i % 2 === 0 ? C.teal : C.gold}`,
                  }}
                >
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: 8,
                      background: C.tealPale,
                      border: `1px solid ${C.teal}33`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.3rem", marginBottom: "1.1rem",
                    }}
                  >
                    {s.icon}
                  </div>
                  <h3
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: "1.3rem", fontWeight: 600, color: C.charcoal, marginBottom: "0.7rem",
                    }}
                  >
                    {s.title}
                  </h3>
                  <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.75, marginBottom: "1.1rem" }}>
                    {s.desc}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {s.tags.map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARATIF ── */}
      <section style={{ padding: isMobile ? "4rem 1.3rem" : "6rem 2rem", background: C.cream }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr",
            gap: isTablet ? "3rem" : "5rem",
            alignItems: "start",
          }}
        >
          <FadeIn>
            <span
              style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: C.gold, display: "block", marginBottom: "0.8rem",
              }}
            >
              Pourquoi nous choisir
            </span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2rem" : "clamp(2rem, 3.5vw, 2.8rem)",
                fontWeight: 600, lineHeight: 1.2, color: C.charcoal, marginBottom: "1.2rem",
              }}
            >
              Ce qui nous distingue
            </h2>
            <p style={{ color: C.textMuted, lineHeight: 1.9, marginBottom: "2.5rem" }}>
              Nous ne sommes pas un prestataire de plus. Nous sommes le partenaire tech que vous
              n'avez jamais eu — disponible, honnête et impliqué.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "1rem",
              }}
            >
              {[
                { icon: "🎯", t: "Spécialisés PME", d: "Nous comprenons vos contraintes réelles : budget, temps, ressources limitées." },
                { icon: "⏱️", t: "Réactivité garantie", d: "Réponse sous 48h. Pas de tickets ouverts des semaines." },
                { icon: "💰", t: "Prix justes", d: "Des tarifs clairs et adaptés. Pas de surprise, pas de frais cachés." },
                { icon: "🧠", t: "Expertise réelle", d: "Une équipe passionnée, à jour sur les dernières technologies." },
              ].map((f, i) => (
                <div
                  key={i}
                  className="hover-card"
                  style={{
                    padding: "1.3rem",
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    transition: "all 0.3s",
                  }}
                >
                  <span style={{ fontSize: "1.4rem", display: "block", marginBottom: "0.6rem" }}>
                    {f.icon}
                  </span>
                  <h4 style={{ fontSize: 13.5, fontWeight: 600, color: C.charcoal, marginBottom: "0.3rem" }}>
                    {f.t}
                  </h4>
                  <p style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.6 }}>{f.d}</p>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={isTablet ? 0 : 0.15}>
            <div
              style={{
                background: C.white,
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                overflow: "hidden",
                boxShadow: `0 4px 20px ${C.charcoal}08`,
                overflowX: "auto",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 320 }}>
                <thead>
                  <tr style={{ background: C.charcoal }}>
                    <th style={{ padding: "0.85rem 1rem", fontSize: 11, fontWeight: 600, color: `${C.cream}88`, textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "left" }}>Critère</th>
                    <th style={{ padding: "0.85rem 0.8rem", fontSize: 11, fontWeight: 600, color: `${C.cream}77`, textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center" }}>Grande ESN</th>
                    <th style={{ padding: "0.85rem 0.8rem", fontSize: 11, fontWeight: 600, color: C.gold, textTransform: "uppercase", letterSpacing: "0.1em", textAlign: "center" }}>ShalvaTech</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map(([crit, bad, good], i) => (
                    <tr
                      key={i}
                      className="hover-row"
                      style={{ background: i % 2 === 0 ? C.cream : C.white, transition: "background 0.15s" }}
                    >
                      <td style={{ padding: "0.85rem 1rem", fontSize: isMobile ? 12 : 13, color: C.textMuted, borderBottom: `1px solid ${C.border}88` }}>{crit}</td>
                      <td style={{ padding: "0.85rem 0.8rem", fontSize: 12.5, color: "#B0A898", textAlign: "center", borderBottom: `1px solid ${C.border}88`, textDecoration: "line-through" }}>{bad}</td>
                      <td style={{ padding: "0.85rem 0.8rem", fontSize: 12.5, color: C.teal, fontWeight: 600, textAlign: "center", borderBottom: `1px solid ${C.border}88` }}>{good}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── PROCESSUS ── */}
      <section
        id="approche"
        style={{ padding: isMobile ? "4rem 1.3rem" : "6rem 2rem", background: C.charcoal }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn style={{ textAlign: "center", maxWidth: 560, margin: "0 auto 4rem" }}>
            <span
              style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: C.gold, display: "block", marginBottom: "0.8rem",
              }}
            >
              Notre approche
            </span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2rem" : "clamp(2rem, 3.5vw, 2.8rem)",
                fontWeight: 600, lineHeight: 1.2, color: C.cream, marginBottom: "1rem",
              }}
            >
              Simple, clair, efficace
            </h2>
            <p style={{ fontSize: "1rem", color: `${C.cream}77`, lineHeight: 1.8 }}>
              Un processus qui vous met à l'aise à chaque étape — sans jargon, sans surprise.
            </p>
          </FadeIn>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "1fr 1fr" : "repeat(4, 1fr)",
              gap: isMobile ? "2rem 1.5rem" : "1.5rem",
              position: "relative",
            }}
          >
            {/* Connector line — desktop only */}
            {!isTablet && (
              <div
                style={{
                  position: "absolute",
                  top: 36,
                  left: "12.5%",
                  right: "12.5%",
                  height: 1,
                  background: `linear-gradient(90deg, ${C.teal}66, ${C.gold}66)`,
                  zIndex: 0,
                }}
              />
            )}
            {STEPS.map((st, i) => (
              <FadeIn key={i} delay={i * 0.1} style={{ position: "relative", zIndex: 1 }}>
                <div className="hover-step" style={{ textAlign: "center", padding: "0 0.5rem" }}>
                  <div
                    className="step-circle"
                    style={{
                      width: 72, height: 72, borderRadius: "50%",
                      background: C.navyLight,
                      border: `2px solid ${C.teal}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto 1.3rem",
                      fontSize: "1.4rem",
                      transition: "all 0.3s",
                      position: "relative",
                    }}
                  >
                    {st.icon}
                    <span
                      style={{
                        position: "absolute", top: -8, right: -8,
                        width: 22, height: 22, borderRadius: "50%",
                        background: C.gold, color: C.charcoal,
                        fontSize: 9.5, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {st.num}
                    </span>
                  </div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: C.cream, marginBottom: "0.5rem" }}>
                    {st.title}
                  </h4>
                  <p style={{ fontSize: 12.5, color: `${C.cream}77`, lineHeight: 1.65 }}>{st.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        style={{
          padding: isMobile ? "4rem 1.3rem" : "6rem 2rem",
          background: `linear-gradient(135deg, ${C.navy} 0%, ${C.charcoal} 100%)`,
          textAlign: "center",
        }}
      >
        <FadeIn>
          <span
            style={{
              fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
              color: C.gold, display: "block", marginBottom: "0.8rem",
            }}
          >
            Passez à l'action
          </span>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: isMobile ? "2rem" : "clamp(2rem, 4vw, 3.2rem)",
              fontWeight: 600, color: C.cream,
              maxWidth: 620, margin: "0 auto 1rem",
            }}
          >
            Prêt à faire de la technologie votre atout ?
          </h2>
          <p
            style={{
              color: `${C.cream}77`, fontSize: "1rem", lineHeight: 1.8,
              maxWidth: 520, margin: "0 auto 2.5rem",
            }}
          >
            Un premier échange de 30 minutes suffit. Gratuit, sans engagement, et sans jargon.
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => scrollTo("contact")}
              style={{
                background: C.gold, color: C.charcoal,
                padding: "0.9rem 2.2rem", border: "none",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                letterSpacing: "0.06em", textTransform: "uppercase",
                borderRadius: 4, transition: "all 0.3s",
                width: isMobile ? "100%" : "auto",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.goldLight;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = C.gold;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Prendre rendez-vous gratuitement
            </button>
            <button
              onClick={() => scrollTo("services")}
              style={{
                background: "transparent", color: C.cream,
                padding: "0.9rem 2.2rem",
                border: `1px solid ${C.cream}44`,
                fontSize: 13, cursor: "pointer",
                letterSpacing: "0.06em", textTransform: "uppercase",
                borderRadius: 4, transition: "all 0.3s",
                width: isMobile ? "100%" : "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.cream)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${C.cream}44`)}
            >
              Voir tous nos services
            </button>
          </div>
        </FadeIn>
      </section>

      {/* ── CONTACT ── */}
      <section
        id="contact"
        style={{ padding: isMobile ? "4rem 1.3rem" : "6rem 2rem", background: C.cream }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn style={{ maxWidth: 580, marginBottom: "3.5rem" }}>
            <span
              style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                color: C.gold, display: "block", marginBottom: "0.8rem",
              }}
            >
              Contact
            </span>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: isMobile ? "2rem" : "clamp(2rem, 3.5vw, 2.8rem)",
                fontWeight: 600, lineHeight: 1.2, color: C.charcoal, marginBottom: "0.8rem",
              }}
            >
              Parlons de votre projet
            </h2>
            <p style={{ color: C.textMuted, lineHeight: 1.8 }}>
              Décrivez-nous votre situation et nous reviendrons vers vous dans les 24h avec des
              premiers éléments de réponse.
            </p>
          </FadeIn>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTablet ? "1fr" : "1.2fr 0.8fr",
              gap: isTablet ? "2.5rem" : "4rem",
              alignItems: "start",
            }}
          >
            <FadeIn>
              {sent ? (
                <div
                  style={{
                    background: C.tealPale, border: `1px solid ${C.teal}44`,
                    borderRadius: 10, padding: "2.5rem", textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "2.5rem", marginBottom: "0.8rem" }}>✉️</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: C.charcoal, marginBottom: "0.5rem" }}>
                    Message envoyé !
                  </h3>
                  <p style={{ color: C.textMuted, fontSize: 13.5 }}>
                    Nous vous recontacterons dans les 24h. Merci de votre confiance.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "1rem",
                    }}
                  >
                    {[
                      ["prenom", "Prénom", "Jean"],
                      ["nom", "Nom", "Dupont"],
                    ].map(([k, l, ph]) => (
                      <div key={k} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          {l}
                        </label>
                        <input
                          value={form[k]}
                          onChange={(e) => setF(k, e.target.value)}
                          placeholder={ph}
                          required
                          style={{
                            padding: "0.65rem 0.9rem", border: `1px solid ${C.border}`,
                            borderRadius: 6, fontSize: 13, color: C.charcoal,
                            background: C.white, fontFamily: "inherit", transition: "border 0.2s",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Email professionnel
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setF("email", e.target.value)}
                      placeholder="jean@monentreprise.com"
                      required
                      style={{
                        padding: "0.65rem 0.9rem", border: `1px solid ${C.border}`,
                        borderRadius: 6, fontSize: 13, color: C.charcoal,
                        background: C.white, fontFamily: "inherit",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Entreprise
                      </label>
                      <input
                        value={form.societe}
                        onChange={(e) => setF("societe", e.target.value)}
                        placeholder="Ma société"
                        style={{
                          padding: "0.65rem 0.9rem", border: `1px solid ${C.border}`,
                          borderRadius: 6, fontSize: 13, color: C.charcoal,
                          background: C.white, fontFamily: "inherit",
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Service souhaité
                      </label>
                      <select
                        value={form.service}
                        onChange={(e) => setF("service", e.target.value)}
                        style={{
                          padding: "0.65rem 0.9rem", border: `1px solid ${C.border}`,
                          borderRadius: 6, fontSize: 13, color: C.charcoal,
                          background: C.white, fontFamily: "inherit",
                        }}
                      >
                        <option value="">Choisir...</option>
                        {SERVICES.map((s) => (
                          <option key={s.title} value={s.title}>{s.title}</option>
                        ))}
                        <option value="Plusieurs / Je ne sais pas">Plusieurs / Je ne sais pas</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Votre message
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setF("message", e.target.value)}
                      placeholder="Décrivez votre activité et ce que vous souhaitez améliorer..."
                      style={{
                        padding: "0.65rem 0.9rem", border: `1px solid ${C.border}`,
                        borderRadius: 6, fontSize: 13, color: C.charcoal,
                        background: C.white, fontFamily: "inherit",
                        minHeight: 120, resize: "vertical",
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    style={{
                      alignSelf: isMobile ? "stretch" : "flex-start",
                      background: C.charcoal, color: C.cream,
                      padding: "0.85rem 2rem", border: "none",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      letterSpacing: "0.06em", textTransform: "uppercase",
                      borderRadius: 4, transition: "all 0.3s",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.teal;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = C.charcoal;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    Envoyer le message →
                  </button>
                </form>
              )}
            </FadeIn>
            <FadeIn delay={isTablet ? 0 : 0.15}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <h3
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "1.5rem", fontWeight: 600, color: C.charcoal, marginBottom: "0.3rem",
                  }}
                >
                  On est là pour vous
                </h3>
                <p style={{ color: C.textMuted, fontSize: 13.5, lineHeight: 1.75, marginBottom: "0.5rem" }}>
                  Pas de formulaire qui disparaît dans un vide. Vous recevrez une réponse
                  personnalisée, par une vraie personne.
                </p>
                {[
                  { icon: "✉", title: "Email", val: "contact@shalvatech.com" },
                  // { icon: "📞", title: "Téléphone & WhatsApp", val: "+33 1 00 00 00 00" },
                  { icon: "🕐", title: "Disponibilité", val: "Lun–Ven · 9h–18h · Réponse sous 48h" },
                  { icon: "📍", title: "Zone d'intervention", val: "France entière (100% en distanciel)" },
                ].map((inf, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex", gap: "0.9rem", padding: "1rem",
                      background: C.white, border: `1px solid ${C.border}`, borderRadius: 7,
                    }}
                  >
                    <span style={{ fontSize: "1.1rem", marginTop: 1 }}>{inf.icon}</span>
                    <div>
                      <h5 style={{ fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.gold, fontWeight: 600, marginBottom: 3 }}>
                        {inf.title}
                      </h5>
                      <p style={{ fontSize: 13, color: C.charcoal }}>{inf.val}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        style={{
          background: C.creamDark,
          borderTop: `1px solid ${C.gold}22`,
          padding: isMobile ? "2rem 1.3rem" : "2.5rem 2rem",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            flexWrap: "wrap",
            gap: "1.2rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img width="90px" src={ShalvaTechLogo} alt="ShalvaTech" />
          </div>
          <div
            style={{
              display: "flex",
              gap: isMobile ? "1rem" : "2rem",
              flexWrap: "wrap",
            }}
          >
            {["Services", "À propos", "Approche", "Contact"].map((l) => (
              <button
                key={l}
                onClick={() => scrollTo(l.toLowerCase().replace("à", "a"))}
                style={{
                  background: "none", border: "none",
                  fontSize: 12.5, color: C.charcoal,
                  cursor: "pointer", transition: "color 0.2s",
                  padding: 0,
                }}
                onMouseEnter={(e) => (e.target.style.color = C.gold)}
                onMouseLeave={(e) => (e.target.style.color = C.charcoal)}
              >
                {l}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: C.charcoal }}>
            © 2025 ShalvaTech · Tous droits réservés
          </span>
        </div>
      </footer>
    </div>
  );
}
